import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {
  Button,
  Checkbox,
  ControlLabel,
  Divider,
  HelperText,
  Stack,
  Typography,
} from 'src/app/components'
import {InputSelectAsync, InputTextField, InputTimePicker} from 'src/app/components/HookForm'
import {DeleteIcon} from 'src/app/components/Icons'
import {getPaymentCategory} from 'src/app/modules/settings/services/PaymentCategory.service'
import {getPaymentMethod} from 'src/app/modules/settings/services/PaymentMethod.service'
import SalesPromoPrize from './SalesPromoPrize.page'
import {PaymentCategoryModel} from 'src/app/modules/settings/models/PaymentCategory.model'
import {PaymentMethodModel} from 'src/app/modules/settings/models/PaymentMethod.model'
import SelectAllCheckbox from 'src/app/components/Inputs/SelectAllCheckbox/SelectAllCheckbox.page'
import {ChangeEvent, useState} from 'react'
import {defaultSalesPromoPrizeTranx, LengthsType} from '../SalesPromoForm3.hook'
import {
  SalesPromoRuleMasterModel,
  SalesPromoRuleModel,
  TransactionRule,
} from 'src/app/modules/campaign/models/SalesPromo.model'
import InputRadioGroupSimple from 'src/app/components/HookForm/InputRadioGroup/InputRadioGroupSimple.page'

export interface SalesPromoTransactionProp {
  storeIndex: number
  id?: string
  prizeRuleLength?: LengthsType
  is_direct_cashback_point?: boolean
  is_mission_collect_stamp?: boolean
  state?: any
  campaignStatus?: string
}

const dayMaster = [
  'Everyday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

export const SalesPromoTransaction = (props: Readonly<SalesPromoTransactionProp>) => {
  const controlName = `prize_rules.${props.storeIndex}.sales_promo_rules`
  const form = useFormContext() // Get the full form object
  const {control, setValue, getValues, clearErrors, reset} = form
  const {fields: rules, append: addRule} = useFieldArray({
    control: control,
    name: `prize_rules.${props.storeIndex}.sales_promo_rules`,
  })

  const errors = form.formState.errors as any
  const selectedTrx = useWatch({control, name: controlName})
  const [store_categories_length, set_store_categories_length] = useState(0)

  const deletePrize = (delIndex: number) => {
    const currentData = getValues()
    const newValueTrx = selectedTrx.filter((_: any, index: number) => index !== delIndex)
    const updatedSalesPromoData = {
      prize_rules: currentData.prize_rules.map((store: SalesPromoRuleModel, sIndex: number) =>
        sIndex === props.storeIndex
          ? {
              ...store,
              sales_promo_rules: newValueTrx,
            }
          : store
      ),
    }
    reset(updatedSalesPromoData)
  }

  const handlePaymentCatChange = (data: PaymentCategoryModel[], index: number, state?: any) => {
    // Get existing payment categories from original database data, not current form state
    const existing = state?.rules?.data?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[index]?.payment_categories
    const deleted = (existing ?? []).filter(
      (e: PaymentCategoryModel) => !data.some((v) => v.id === e.id)
    )

    if (deleted?.length > 0 && ['ACTIVE', 'INACTIVE'].includes(props.campaignStatus ?? '')) {
      form.setError(`${controlName}.${index}.payment_categories`, {
        message: 'Existing payment category cannot be removed',
        type: 'required',
      })
      // Restore by maintaining original order from database
      const allIds = new Set([...data.map((d: PaymentCategoryModel) => d.id), ...deleted.map((d: PaymentCategoryModel) => d.id)])
      const restoredValue = [
        ...(existing ?? []).filter((e: PaymentCategoryModel) => allIds.has(e.id)), // Keep existing items in original order
        ...data.filter((d: PaymentCategoryModel) => !(existing ?? []).some((e: PaymentCategoryModel) => e.id === d.id)) // Add new items at the end
      ]
      setValue(`${controlName}.${index}.payment_categories`, restoredValue)
    } else {
      const selectedCategoryIds = data?.map((_: PaymentCategoryModel) => _?.id) || []
      clearErrors(`${controlName}.${index}.payment_categories`)
      setValue(`${controlName}.${index}.payment_category_ids`, selectedCategoryIds)

      // Only clear payment methods if categories are being reduced compared to database data
      const isReducingCategories = data.length < (existing?.length || 0)

      if (isReducingCategories) {
        // Clear payment methods when removing categories to avoid invalid selections
        setValue(`${controlName}.${index}.payment_methods`, [])
        setValue(`${controlName}.${index}.payment_method_ids`, [])
      }
      // If adding categories, keep existing payment methods
    }
  }

  const handlePaymentMedChange = (data: PaymentMethodModel[], index: number) => {
    // Get existing payment methods from original database data, not current form state
    const existing = props.state?.rules?.data?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[index]?.payment_method_ids
    const dataId = data?.map((_: PaymentMethodModel) => _?.id)
    const deleted = (existing ?? []).filter(
      (e: PaymentCategoryModel) => !dataId.some((v) => v === e)
    )
    if (deleted?.length > 0 && ['ACTIVE', 'INACTIVE'].includes(props.campaignStatus ?? '')) {
      form.setError(`${controlName}.${index}.payment_methods`, {
        message: 'Existing payment by cannot be removed',
        type: 'required',
      })
      // Restore by maintaining original order from database
      const existingMethods = props.state?.rules?.data?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[index]?.payment_methods || []
      const allIds = new Set([...dataId, ...deleted])
      const restoredValue = [
        ...existingMethods.filter((m: PaymentMethodModel) => allIds.has(m.id)), // Keep existing items in original order
        ...data.filter((d: PaymentMethodModel) => !existingMethods.some((m: PaymentMethodModel) => m.id === d.id)) // Add new items at the end
      ]
      setValue(`${controlName}.${index}.payment_methods`, restoredValue)
      setValue(`${controlName}.${index}.payment_method_ids`, restoredValue.map((m: PaymentMethodModel) => m.id))
    } else {
      clearErrors(`${controlName}.${index}.payment_methods`)
      setValue(
        `${controlName}.${index}.payment_method_ids`,
        data?.map((_: PaymentMethodModel) => _?.id)
      )
    }

    // `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_methods`
  }

  const handleSyncCheckedAllStore = async (key: boolean, index: number) => {
    try {
      let tempDataPaymentBy: PaymentMethodModel[] = []
      if (key) {
        const paymentCategoryIds = getValues(
          `prize_rules.${props.storeIndex}.sales_promo_rules.${index}.payment_category_ids`
        )
        const storeCategoriesResponse = await getPaymentMethod({
          payment_category_ids: paymentCategoryIds,
          statuses: ['ACTIVE'],
          is_dropdown: true,
          limit: 2000,
        })
        const storeCategoriesData =
          storeCategoriesResponse?.data?.response_output?.list?.content ?? []
        tempDataPaymentBy = storeCategoriesData
        set_store_categories_length(storeCategoriesData.length)
      }
      const paymentMethodIds = tempDataPaymentBy.map((item) => item.id)
      setValue(`${controlName}.${index}.payment_method_ids`, paymentMethodIds)
      setValue(`${controlName}.${index}.payment_methods`, tempDataPaymentBy)
      clearErrors(`${controlName}.${index}.payment_methods`)
    } catch (error) {
      console.error('Error in handleSyncCheckedAllStore:', error)
    }
  }

  // cycle everyday
  const handleChangeDay = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const days = getValues(`prize_rules.${props.storeIndex}.sales_promo_rules.${index}.days`)

    const _value = event.target.value
    if (event.target.checked) {
      if (_value === 'EVERYDAY' || days.length >= dayMaster.length - 2) {
        form.setValue(`${controlName}.${index}.days`, ['EVERYDAY'], {shouldValidate: true})
      } else {
        const _days = days.filter((_: any) => _ !== 'EVERYDAY')
        form.setValue(`${controlName}.${index}.days`, [..._days, _value], {shouldValidate: true})
      }
    } else {
      const _days = days.filter((_: any) => _ !== _value)
      form.setValue(`${controlName}.${index}.days`, _days, {shouldValidate: true})
    }
  }

  // const daysDisabled = useMemo(
  //     () => !!enabledFields && !enabledFields.includes('days'),
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //     [state.rewards.data?.reward_status],
  // )
  const renderDayItem = (item: string, index: number) => {
    const days: string[] = getValues(
      `prize_rules.${props.storeIndex}.sales_promo_rules.${index}.days`
    )
    const value = item.toUpperCase()
    const isChecked = days.includes(value)
    return (
      <Checkbox
        // disabled={daysDisabled}
        checked={isChecked}
        label={item}
        value={value}
        key={item}
        onChange={(e) => handleChangeDay(e, index)}
      />
    )
  }

  const getDisabled = (index: number): boolean => {
    const prizeRules = props.prizeRuleLength?.prize_rules
    if (!prizeRules || props.storeIndex === undefined || !prizeRules[props.storeIndex]) {
      return false
    }

    return (prizeRules[props.storeIndex].sales_promo_rules_length ?? 0) > index
  }

  return (
    <>
      {rules.map((_, ruleIndex) => (
        <Stack
          key={`rules-${ruleIndex}`}
          className='p-6 border border border-neutral-300 rounded-lg'
        >
          <div className='flex items-center justify-between'>
            <Typography variant='h5'>Transaction {ruleIndex + 1}</Typography>
            <Button
              disabled={rules.length < 2 || getDisabled(ruleIndex)}
              startIcon={DeleteIcon}
              variant='text'
              color='danger'
              onClick={() => deletePrize(ruleIndex)}
            >
              Delete
            </Button>
          </div>

          <ControlLabel label='Min. Amount / Receipt' required={true}>
            <Stack direction='row'>
              <div className='w-full'>
                <InputTextField
                  placeholder='000.000.000'
                  name={`${controlName}.${ruleIndex}.minimum_transaction`}
                  type='currency'
                  rule={{
                    required: 'Field cannot be empty.',
                    min: {
                      value: 1,
                      message: 'Minimum total transaction is 1',
                    },
                    max: {
                      value: 999999999,
                      message: 'Maximum total transaction is 999.999.999',
                    },
                  }}
                  disabled={getDisabled(ruleIndex)}
                  error={
                    errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                      ?.minimum_transaction?.message
                  }
                  helperText={
                    errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                      ?.minimum_transaction?.message
                  }
                />
              </div>
              {!props.is_direct_cashback_point && (
                <div className='min-w-[188px] h-[42px] content-center'>
                  <Controller
                    control={control}
                    name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_multiplication_single_receipt`}
                    render={({field}) => (
                      <Checkbox
                        label='Multiplication Applied'
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={
                          getDisabled(ruleIndex) ||
                          getValues(
                            `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_combine_receipt`
                          )
                        }
                      />
                    )}
                  />
                </div>
              )}
            </Stack>
          </ControlLabel>

          <ControlLabel label='Combine Receipt' required>
            <InputRadioGroupSimple
              name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_combine_receipt`}
              options={[
                {label: 'Yes', value: true},
                {label: 'No', value: false},
              ]}
              required
              onChange={(_) => {
                if (_)
                  setValue(
                    `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_multiplication_single_receipt`,
                    false
                  )
              }}
              disabled={getDisabled(ruleIndex)}
            />
          </ControlLabel>

          {getValues(
            `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_combine_receipt`
          ) && (
            <>
              <ControlLabel label='Max. Number of Combine Receipt' required={true}>
                <InputTextField
                  placeholder='Enter max. Number of Combine Receipt'
                  name={`${controlName}.${ruleIndex}.max_combine_receipt`}
                  type='number'
                  min={0}
                  rule={{
                    required: 'Field cannot be empty.',
                    min: {
                      value: 1,
                      message: 'Minimum max combine receipt is 1',
                    },
                    max: {
                      value: 999999999,
                      message: 'Maximum max combine receipt is 999.999.999',
                    },
                  }}
                  disabled={getDisabled(ruleIndex)}
                  error={
                    errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                      ?.max_combine_receipt?.message
                  }
                  helperText={
                    errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                      ?.max_combine_receipt?.message
                  }
                />
              </ControlLabel>

              <ControlLabel label='Min. Total Spend Combine Receipt' required={true}>
                <Stack direction='row'>
                  <div className='w-full'>
                    <InputTextField
                      placeholder='000.000.000'
                      name={`${controlName}.${ruleIndex}.min_combine_total_amount`}
                      type='currency'
                      rule={{
                        required: 'Field cannot be empty.',
                        min: {
                          value: 1,
                          message: 'Minimum total amount is 1',
                        },
                        max: {
                          value: 999999999,
                          message: 'Maximum total amount is 999.999.999',
                        },
                      }}
                      disabled={getDisabled(ruleIndex)}
                      error={
                        errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                          ?.min_combine_total_amount?.message
                      }
                      helperText={
                        errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                          ?.min_combine_total_amount?.message
                      }
                    />
                  </div>
                  <div className='min-w-[188px] h-[42px] content-center'>
                    <Controller
                      control={control}
                      name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_multiplication_combine_receipt`}
                      render={({field}) => (
                        <Checkbox
                          label='Multiplication Applied'
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </div>
                </Stack>
              </ControlLabel>
            </>
          )}

          <ControlLabel label='Payment Category' required={true}>
            <InputSelectAsync
              onChangeValue={(e) =>
                handlePaymentCatChange(e as PaymentCategoryModel[], ruleIndex, props.state)
              }
              placeholder='Select Payment Category'
              fetchApi={getPaymentCategory}
              payload={{
                statuses: ['ACTIVE'],
                is_dropdown: true,
                limit: 2000,
              }}
              isMulti={true}
              form={form}
              rule={{required: true}}
              // disabled={getDisabled(ruleIndex)}
              name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_categories`}
              selectOption={(_) => ({
                label: _.payment_category_name!,
                value: _.id,
              })}
            />
          </ControlLabel>
          <SelectAllCheckbox
            label='Select All Payment by'
            onChecked={(_) => {
              handleSyncCheckedAllStore(true, ruleIndex)
            }}
            onUnChecked={() => {
              handleSyncCheckedAllStore(false, ruleIndex)
            }}
            checked={
              form.watch(
                `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_methods`
              )?.length !== 0 &&
              form.watch(
                `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_methods`
              )?.length === store_categories_length
            }
            disabled={
              getDisabled(ruleIndex) ||
              form.watch(
                `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_categories`
              )?.length < 1
            }
            selectOption={(_) => ({
              label: _._!,
              value: _._!,
            })}
          />
          <ControlLabel label='Payment By' required={true}>
            <InputSelectAsync
              onChangeValue={(e) => handlePaymentMedChange(e as PaymentMethodModel[], ruleIndex)}
              placeholder='Select payment by'
              fetchApi={getPaymentMethod}
              payload={{
                payment_category_ids: getValues(
                  `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_category_ids`
                ),
                statuses: ['ACTIVE'],
                is_dropdown: true,
                limit: 2000,
              }}
              isMulti={true}
              form={form}
              rule={{required: true}}
              disabled={
                // getDisabled(ruleIndex) ||
                form.watch(
                  `prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_categories`
                )?.length < 1
              }
              name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.payment_methods`}
              selectOption={(_) => ({
                label: _.method_name!,
                value: _.id,
              })}
            />
          </ControlLabel>

          <Controller
            name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.days`}
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Field cannot be empty',
              },
            }}
            render={() => (
              <ControlLabel label='Day' required={true}>
                <div>
                  <div className='grid grid-cols-4 gap-4'>
                    {dayMaster.map((data: string) => renderDayItem(data, ruleIndex))}
                  </div>
                  <HelperText
                    error={
                      !!errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]
                        ?.days
                    }
                  >
                    {
                      errors?.prize_rules?.[props.storeIndex]?.sales_promo_rules?.[ruleIndex]?.days
                        ?.message
                    }
                  </HelperText>
                </div>
              </ControlLabel>
            )}
          />

          <ControlLabel label='Hour' required={true}>
            <Stack direction='row' items='center'>
              <InputTimePicker
                placeholder='Select start hour'
                className='flex-1'
                rule={{required: true}}
                name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.start_time`}
              />
              <Divider className='w-[24px] border-t-2' />
              <InputTimePicker
                placeholder='Select end hour'
                className='flex-1'
                rule={{required: true}}
                name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.end_time`}
              />
            </Stack>
          </ControlLabel>

          {!props.is_mission_collect_stamp && (
            <ControlLabel label='Mixable Gift/Voucher' required>
              <InputRadioGroupSimple
                name={`prize_rules.${props.storeIndex}.sales_promo_rules.${ruleIndex}.is_mixable`}
                options={[
                  {label: 'Yes', value: true},
                  {label: 'No', value: false},
                ]}
                required
                disabled={getDisabled(ruleIndex)}
              />
            </ControlLabel>
          )}
          {!props.is_direct_cashback_point && (
            <ControlLabel label='Prize' required={true}>
              <SalesPromoPrize
                id={props.id}
                storeIndex={props.storeIndex}
                ruleIndex={ruleIndex}
                prizeRuleLength={props.prizeRuleLength}
                is_mission_collect_stamp={props.is_mission_collect_stamp}
              />
            </ControlLabel>
          )}

          {rules.length - 1 === ruleIndex && (
            <ControlLabel label=''>
              <Button variant='outlined' onClick={() => addRule(defaultSalesPromoPrizeTranx)}>
                Add New Transaction
              </Button>
            </ControlLabel>
          )}
        </Stack>
      ))}
    </>
  )
}
